import { Injectable } from '@angular/core';
import { ScopePack } from '../models/chat.model';
import { AnalyticsService } from './analytics.service';
import { CLIENT_EVENTS, GA4_EVENTS } from './analytics.events';
import { EventProperties } from './analytics.types';

/**
 * Scope-funnel tracking — the highest-priority funnel in the product.
 *
 * WHY THIS IS A SEPARATE CLASS
 * ────────────────────────────────────────────────────────────────────────
 * The scope conversation is driven by ChatService, which is ~900 lines and
 * is the single most business-critical service in the app. Putting session
 * bookkeeping (has this scope started? how many turns? how long?) and
 * ScopePack field-mapping inline would add a third responsibility to a file
 * that already has two, for a feature that must never affect scoping.
 *
 * Keeping it here means ChatService gains a few one-line calls, and the
 * mapping logic below can be reasoned about (and tested) on its own.
 *
 * WHY IT LIVES IN THE SERVICE AND NOT THE COMPONENTS
 * ────────────────────────────────────────────────────────────────────────
 * Ten different components drive ChatService — the customer console, the
 * internal console, the chatbot, the home page, the brief panels. Firing
 * these events from the components would mean ten instrumentation sites that
 * must be kept in step, and a scope started from a newly-added entry point
 * would silently go unmeasured. One service, one set of events.
 *
 * NOTHING HERE MAY BREAK SCOPING
 * ────────────────────────────────────────────────────────────────────────
 * Every public method swallows its own errors. A client mid-brief must never
 * see a failure because an analytics property was undefined.
 */
@Injectable({ providedIn: 'root' })
export class ScopeFunnelTracker {
  /** Null when no scope is in progress. */
  private session: {
    startedAt: number;
    /** Client turns — the doc's "Number of Questions". */
    turns: number;
    completed: boolean;
    /** Set once so a resumed/edited scope cannot re-fire `scope_started`. */
    source: string;
    /**
     * Within-scope milestones already reported this session.
     *
     * The pack is re-emitted on every edit, so without this the budget event
     * would fire again on each subsequent turn and the step would out-count
     * the scope that contains it.
     */
    milestones: Set<string>;
  } | null = null;

  constructor(private analytics: AnalyticsService) {}

  /**
   * First client message of a scoping conversation.
   *
   * Deliberately fired on the first MESSAGE, not on page load: opening the
   * brief screen and leaving is not "starting a scope", and counting it as
   * one would make the funnel's first step meaningless and inflate the
   * abandonment rate against a denominator of window-shoppers.
   */
  scopeStarted(source: string, seed?: EventProperties): void {
    try {
      if (this.session && !this.session.completed) return;  // already running
      this.session = {
        startedAt: Date.now(), turns: 1, completed: false, source,
        milestones: new Set<string>(),
      };
      this.analytics.track(CLIENT_EVENTS.SCOPE_STARTED, { source, ...(seed || {}) });
      // GA4 gets the marketing-facing equivalents. `generate_lead` is a
      // recommended GA4 conversion and is what Google Ads optimises against,
      // so the scope-start signal has to reach it under that name.
      this.analytics.track(GA4_EVENTS.BEGIN_SCOPE, { source });
      this.analytics.track(GA4_EVENTS.GENERATE_LEAD, { source });
    } catch { /* never break scoping */ }
  }

  /** A subsequent client turn. Feeds the doc's "Number of Questions". */
  clientTurn(): void {
    try {
      if (this.session && !this.session.completed) this.session.turns += 1;
    } catch { /* never break scoping */ }
  }

  /**
   * The AI produced a usable scope pack — the moment the funnel converts.
   *
   * Two events on purpose. `ai_scope_generated` measures the AI step;
   * `scope_completed` measures the business outcome. They coincide today,
   * but if a review/confirm step is ever added between them, the funnel
   * keeps working without re-instrumentation.
   */
  scopeGenerated(pack: ScopePack | null): void {
    try {
      if (!this.session || this.session.completed) return;
      const packProps = this.packProperties(pack);
      // Before the completion events below, so the funnel reads in the order
      // the steps actually happened: budget and timeline are part of reaching
      // a scope, not something that follows it.
      this.detectMilestones(pack);
      // Typed explicitly: spreading EventProperties into an object literal
      // makes TypeScript infer only the literal's own keys, so reading
      // props['service'] below would be an implicit-any index error.
      const props: EventProperties = {
        ...packProps,
        number_of_questions: this.session.turns,
        time_taken_seconds: this.elapsedSeconds(),
        source: this.session.source,
      };
      this.session.completed = true;
      this.analytics.track(CLIENT_EVENTS.AI_SCOPE_GENERATED, props);
      this.analytics.track(CLIENT_EVENTS.SCOPE_COMPLETED, props);

      // Persist what this client buys onto their profile.
      //
      // These are the GA4 doc's "custom dimensions" — service, sub-service,
      // city, budget band — and a scope is the first and often only place the
      // product learns any of them. Without this they exist on one event and
      // nowhere else, so "which services convert best" can only be answered by
      // replaying event history rather than by segmenting users.
      //
      // Budget is stored as a BAND, not the number. Segmentation needs buckets,
      // and an exact figure is both unusable as a segment and needlessly
      // sensitive to hold on a profile.
      this.analytics.setUserProperties({
        last_service: packProps['service'] as string,
        last_sub_service: packProps['sub_service'] as string,
        city: (packProps['city'] as string) || undefined,
        last_budget_band: this.budgetBand(packProps['budget']),
      });
      // GA4 gets only the two dimensions marketing reports on. Sending the
      // full property set would burn custom-dimension slots, which GA4 caps.
      this.analytics.track(GA4_EVENTS.COMPLETE_SCOPE, {
        service: packProps['service'],
        sub_service: packProps['sub_service'],
      });
    } catch { /* never break scoping */ }
  }

  /** The client changed a scope after it was generated. */
  scopeEdited(pack: ScopePack | null, reason: string): void {
    try {
      // An edit is how a budget or deadline usually arrives — the first pack
      // often has neither. Without this, the two steps would only ever be seen
      // on scopes that answered them before the pack was first generated.
      this.detectMilestones(pack);
      this.analytics.track(CLIENT_EVENTS.SCOPE_EDITED, {
        ...this.packProperties(pack),
        reason,
      });
    } catch { /* never break scoping */ }
  }

  /** The brief was explicitly saved (not the background autosave). */
  scopeSaved(pack: ScopePack | null, projectId?: string): void {
    try {
      this.analytics.track(CLIENT_EVENTS.SCOPE_SAVED, {
        ...this.packProperties(pack),
        project_id: projectId || null,
      });
    } catch { /* never break scoping */ }
  }

  /**
   * Started but never completed.
   *
   * This is the event the CEO deck's "12 clients abandoned scope" line and
   * the "42% drop at the budget question" example both depend on, and it is
   * the only one here that cannot be inferred later: once the tab closes,
   * the in-progress state is gone. `turns` is carried so a scope abandoned
   * on turn 1 can be told apart from one abandoned on turn 9 — a bounce and
   * a genuine loss are different problems.
   */
  scopeAbandoned(reason: 'navigated_away' | 'session_reset' | 'page_closed'): void {
    try {
      if (!this.session || this.session.completed) return;
      const { turns, source } = this.session;
      // Read elapsed BEFORE clearing the session — elapsedSeconds() reads
      // this.session, so clearing first would report null every time.
      const elapsed = this.elapsedSeconds();
      this.session = null;
      this.analytics.track(CLIENT_EVENTS.SCOPE_ABANDONED, {
        reason,
        number_of_questions: turns,
        time_taken_seconds: elapsed,
        source,
      });
    } catch { /* never break scoping */ }
  }

  /** True while a scope is in progress — used to arm the unload handler. */
  get isInProgress(): boolean {
    return !!this.session && !this.session.completed;
  }

  // ── Internals ──────────────────────────────────────────────────────────

  /**
   * The doc's property list — Service, Sub Service, Budget, Timeline, City,
   * Industry — read off the ScopePack.
   *
   * Every read is optional-chained and every value defaults to null rather
   * than being omitted. A missing property in Mixpanel is indistinguishable
   * from an event that never had it, so an explicit null is what lets you
   * see "we generated 40 scopes and 12 had no budget" instead of silence.
   *
   * `industry` is NOT on ScopePack today. It is emitted as null so the
   * property exists on the event from day one — adding a property later
   * leaves every historical event without it, and Mixpanel cannot backfill.
   */
  /**
   * Whether a scope field has actually been answered.
   *
   * NOT `!= null`. `0`, `''` and `false` are all values this pack legitimately
   * carries for an unanswered field, and counting them as answered is a mistake
   * that reads as a near-perfect fill rate while the real one is a fraction of
   * it — the step looks healthy precisely where it is leaking.
   */
  private answered(v: unknown): boolean {
    if (v === null || v === undefined || v === false) return false;
    if (typeof v === 'number') return Number.isFinite(v) && v > 0;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  }

  /**
   * Fire the within-scope steps the moment they are first answered.
   *
   * `scope_abandoned` already says a client left on turn 3; these say WHAT
   * they had given us by then. Together they answer "42% drop at the budget
   * question" — which is the whole reason the scope funnel exists and is not
   * answerable from `number_of_questions` alone.
   *
   * Driven off the pack rather than from ChatService call sites for the reason
   * in the class comment: ten components drive scoping, and an eleventh added
   * later would silently go unmeasured.
   */
  private detectMilestones(pack: ScopePack | null): void {
    // Deliberately NOT gated on `completed`, unlike the other methods here.
    // A scope is usually generated before the client has given a budget, and
    // the figure arrives on a later edit — by which point `completed` is true.
    // Gating on it would miss the budget step for exactly the scopes that have
    // one. The milestones Set is what prevents a repeat, and it is enough.
    if (!this.session || !pack) return;
    const bt = pack.budget_timeline;
    const seen = this.session.milestones;

    const budget = this.answered(bt?.client_budget_value)
                || this.answered(bt?.final_budget_value);
    if (budget && !seen.has('budget')) {
      seen.add('budget');
      this.analytics.track(CLIENT_EVENTS.SCOPE_BUDGET_ADDED, {
        // The BAND, not the figure — same reasoning as the user property.
        budget_band: this.budgetBand(
          bt?.client_budget_value ?? bt?.final_budget_value),
        turn: this.session.turns,
        source: this.session.source,
      });
    }

    const timeline = this.answered(bt?.final_timeline_days)
                  || this.answered(bt?.estimated_timeline_days)
                  || this.answered(bt?.client_deadline_date);
    if (timeline && !seen.has('timeline')) {
      seen.add('timeline');
      this.analytics.track(CLIENT_EVENTS.SCOPE_TIMELINE_ADDED, {
        timeline_days: bt?.final_timeline_days
                    ?? bt?.estimated_timeline_days ?? null,
        has_deadline: this.answered(bt?.client_deadline_date),
        turn: this.session.turns,
        source: this.session.source,
      });
    }
  }

  private packProperties(pack: ScopePack | null): EventProperties {
    const cls = pack?.classification;
    const bt = pack?.budget_timeline;
    const cap = pack?.vendor_capability_summary || pack?.hiring_summary;
    return {
      service: cls?.main_service ?? null,
      sub_service: cls?.sub_service ?? null,
      // Prefer what the CLIENT asked for over what the system settled on —
      // the funnel question is what the client wanted, not what we quoted.
      budget: bt?.client_budget_value ?? bt?.final_budget_value ?? null,
      budget_currency: bt?.client_budget_currency ?? null,
      timeline_days: bt?.final_timeline_days ?? bt?.estimated_timeline_days ?? null,
      deadline_date: bt?.client_deadline_date ?? null,
      city: cap?.location_preference ?? null,
      vendor_type: cap?.vendor_type ?? null,
      experience_band: cap?.experience_band ?? null,
      industry: null,   // see note above
    };
  }

  /**
   * Budget as a band rather than a figure.
   *
   * Segmentation needs buckets: "how do sub-5L clients convert versus 25L+"
   * is answerable, "how does the ₹847,300 client convert" is not. Bands also
   * keep an exact commercial figure off the user profile, where it would sit
   * indefinitely for no analytical gain.
   *
   * Boundaries are in rupees and chosen to match how briefs actually cluster;
   * adjust them here and nowhere else.
   */
  private budgetBand(value: unknown): string | undefined {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    if (n < 100_000) return 'under_1L';
    if (n < 500_000) return '1L_5L';
    if (n < 1_000_000) return '5L_10L';
    if (n < 2_500_000) return '10L_25L';
    return '25L_plus';
  }

  private elapsedSeconds(): number | null {
    return this.session ? Math.round((Date.now() - this.session.startedAt) / 1000) : null;
  }
}
